import React from "react";
import { getCurrencySymbol } from "../../../helper/formatCurrency";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import { getAppConfig } from "../../../utils/appConfig";
import { API_BASE } from "../../../utils/api";

/**
 * Product Table Component
 * Displays selected products with quantity controls, prices, and remove actions
 * Shows empty state when no products are added
 *
 * @param {Object} props
 * @param {Array} props.products - Array of product objects in the order
 * @param {Function} props.updateQuantity - Function to update product quantity (id, qty)
 * @param {Function} props.removeProduct - Function to remove product from order (id)
 */
const ProductTable = ({ products, updateQuantity, removeProduct }) => {
  const { locale } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);
  const { defaultImage } = getAppConfig();
  const getImageSrc = (product) => {
    const path = product.image || product.imageUrl;

    if (path) {
      if (path.startsWith("http")) return path;
      return `${API_BASE}${path}`;
    }

    if (defaultImage) {
      return `${API_BASE}${defaultImage}`;
    }

    return "/img/finglider_logo.jpg";
  };

  if (!products.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        <svg
          className="w-16 h-16 mx-auto mb-3 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <p className="text-sm">No products added yet</p>
        <p className="text-xs text-gray-400 mt-1">
          Search and select products to add them here
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
      <table className="w-full min-w-[600px] sm:min-w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-1 sm:px-2 text-xs sm:text-sm font-semibold text-gray-700">
              Product
            </th>
            <th className="text-right py-3 px-1 sm:px-2 text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
              Price
            </th>
            <th className="text-center py-3 px-1 sm:px-2 text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
              Qty
            </th>
            <th className="text-right py-3 px-1 sm:px-2 text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
              Total
            </th>
            <th className="py-3 px-1 sm:px-2"></th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => (
            <tr
              key={p._id}
              className="border-b border-gray-100 hover:bg-gray-50"
            >
              {/* Product Name & Image */}
              <td className="py-3 px-1 sm:px-2">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <img
                    src={getImageSrc(p)}
                    alt={p.productName}
                    className="w-8 h-8 sm:w-10 sm:h-10 object-cover rounded flex-shrink-0"
                    onError={(e) => {
                      e.target.src = defaultImage
                        ? `${API_BASE}${defaultImage}`
                        : "/img/finglider_logo.jpg";
                    }}
                  />
                  <div className="min-w-0">
                    <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                      {p.productName}
                    </div>
                    {p.packageCharge > 0 && (
                      <div className="text-[10px] sm:text-xs text-gray-500">
                        +{currencySymbol}
                        {p.packageCharge} pkg
                      </div>
                    )}
                  </div>
                </div>
              </td>

              {/* Price */}
              <td className="py-3 px-1 sm:px-2 text-right whitespace-nowrap">
                <div className="text-xs sm:text-sm font-medium text-gray-900">
                  {currencySymbol}
                  {p.finalPrice}
                </div>
                {p.offerPrice && (
                  <div className="text-[10px] sm:text-xs text-gray-400 line-through">
                    {currencySymbol}
                    {p.price}
                  </div>
                )}
              </td>

              {/* Quantity */}
              <td className="py-3 px-1 sm:px-2">
                <div className="flex items-center justify-center gap-1 sm:gap-2">
                  <button
                    onClick={() =>
                      updateQuantity(p._id, Math.max(1, p.quantity - 1))
                    }
                    className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-600 font-semibold text-sm"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={p.quantity === "" ? "" : p.quantity}
                    min="1"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        updateQuantity(p._id, "");
                        return;
                      }
                      const qty = parseInt(value);
                      updateQuantity(p._id, isNaN(qty) ? 0 : qty);
                    }}
                    className="w-10 sm:w-12 text-center border border-gray-300 rounded py-0.5 sm:py-1 text-xs sm:text-sm"
                  />
                  <button
                    onClick={() => updateQuantity(p._id, p.quantity + 1)}
                    className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-600 font-semibold text-sm"
                  >
                    +
                  </button>
                </div>
              </td>

              {/* Total */}
              <td className="py-3 px-1 sm:px-2 text-right whitespace-nowrap">
                <div className="text-xs sm:text-sm font-semibold text-gray-900">
                  {currencySymbol}
                  {(p.quantity * p.finalPrice).toFixed(2)}
                </div>
              </td>

              {/* Remove Button */}
              <td className="py-3 px-1 sm:px-2 text-center">
                <button
                  onClick={() => removeProduct(p._id)}
                  className="p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Remove item"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductTable;
